import type { GasViscosityParameters, GasViscosityCalculator } from "../d";

const getPositiveFiniteOrFallback = (value: number, fallback: number) => {
  return Number.isFinite(value) && value > 0 ? value : fallback;
};

// ===================================================================================

// ===================================================================================

// so we need some integrity checks up front
// C********************************************************************************
// C The region of the CARR et al. representation of viscosity ratio covered is:
// C      .10 < Pr < 20.0
// C     1.05 < Tr <  3.0
// C with the same restriction on Pr in the region
// C     1.05 < Tr <  1.30
// C as are present in the original graphical representation
// C
// C********************************************************************************

//   SUBROUTINE VISC(T, TR, P, G, VISCO, INDEX, H2S, CO2, XN2)
//   EXTERNAL F1, FN2, FCO2, FH2S

// C********************************************************************************
// C  INDEX = 1 ===>  Correction for N2,CO2,SO2 required
// C  INDEX = 0 ===>  Correction for N2,CO2,SO2 not required

// NOTE FROM BEN
// C********************************************************************************
// G - Gas Specific Gravity - ySG
// T - In Situ Temp - F
// P - Psuedo-Reduced Pressure - Ppr
// Tr - Psuedo-Reduced Temperature - Tpr
// H2S - % H2S
// CO2 - % CO2
// N2 - % N2
// Index = 1 if you have any values in either H2S, CO2, N2
// Index = 0 if they are unused

export const carrKobayashiBurrowsGasViscosityCalculator: GasViscosityCalculator =
  params => {
    const {
      temperatureF,
      psuedoCriticals: { psuedoReducedPressure, psuedoReducedTemperature }
    } = params;
    // THESE VARIABLES ARE USED EVERYWHERE
    let VISRAT = 0;
    let X = 0;
    let Y = 0;
    let VTEMP = 0; // eslint-disable-line prefer-const
    let IN = 0;
    // C  VISCO      = Output viscosity at P and TR

    // DIMENSION A(15), B(21), C(10), D(6)
    const A = [
      0.001685, -0.054839, 0.032083, 0.375024, -0.051891, -0.142471, -0.773172,
      0.558395, -0.427826, 0.411381, 0.463066, -0.675948, 0.971044, -0.832204,
      0.17138
    ];
    const B = [
      0.174456, -2.08448, 1.16834, 10.9273, -9.53987, 4.49831, -24.299103,
      19.529404, -0.46506, -8.67977, 23.638702, -13.8533, -11.0101, 8.99483,
      6.49568, -8.34437, 2.446, 9.32751, -7.15457, 1.80986, -3.35092
    ];
    const C = [
      0.468794, -1.39273, 1.10244, -0.358802, 2.32974, -1.23826, 0.155171,
      0.255111, -1.54132, 0.749261
    ];
    const D = [1.443, 1.639, 52.2, -105.019, 131.7, 32.29];
    const H = Math.log(temperatureF);
    // PLIMIT = D(1) + D(2) * H + D(3) * H * H + D(4) *	H ** 3 + D(5) *
    // 1H ** 4 + D(6) * H ** 5
    const PLIMIT =
      D[0] +
      D[1] * H +
      D[2] * H * H +
      D[3] * H ** 3 +
      D[4] * H ** 4 +
      D[5] * H ** 5;

    if (
      psuedoReducedPressure > 1 &&
      psuedoReducedPressure <= PLIMIT &&
      psuedoReducedTemperature <= 1.8 &&
      Math.abs(PLIMIT - psuedoReducedPressure) <= 0.2
    ) {
      IN = 2;
    } else if (
      psuedoReducedPressure >= 0.85 &&
      psuedoReducedPressure <= 1.07 &&
      psuedoReducedTemperature < 1.2
    ) {
      IN = 1;
    } else if (
      psuedoReducedPressure > 1 &&
      psuedoReducedPressure <= PLIMIT &&
      psuedoReducedTemperature < 1.8 &&
      Math.abs(PLIMIT - psuedoReducedPressure) <= 0.2
    ) {
      IN = 2;
    } else if (
      psuedoReducedPressure >= 0.85 &&
      psuedoReducedPressure <= 1.01 &&
      psuedoReducedTemperature < 1.2
    ) {
      IN = 1;
    } else if (
      psuedoReducedPressure > 1.01 &&
      psuedoReducedPressure <= PLIMIT &&
      psuedoReducedTemperature <= 1.8 &&
      Math.abs(PLIMIT - psuedoReducedPressure) <= 0.2
    ) {
      IN = 2;
    } else if (
      psuedoReducedPressure >= 1.01 &&
      psuedoReducedPressure <= 1.07 &&
      psuedoReducedTemperature < 1.2
    ) {
      IN = 1;
    } else if (
      psuedoReducedPressure > 1.01 &&
      psuedoReducedPressure <= PLIMIT &&
      psuedoReducedTemperature < 1.2 &&
      Math.abs(PLIMIT - psuedoReducedPressure) <= 0.2
    ) {
      IN = 2;
    } else {
      IN = 0;
    }

    let gotoSwitch = "goto60";
    if (
      psuedoReducedTemperature > 1.8 &&
      psuedoReducedPressure > 1 &&
      psuedoReducedPressure < 2
    )
      gotoSwitch = "goto40";
    // CONDITION: psuedoReducedTemperature > 1.2 && psuedoReducedTemperature < 1.8 && psuedoReducedPressure < PLIMIT && psuedoReducedPressure > 1
    else if (
      psuedoReducedTemperature > 1.2 &&
      psuedoReducedTemperature < 1.8 &&
      psuedoReducedPressure < PLIMIT &&
      psuedoReducedPressure > 1
    )
      gotoSwitch = "goto40";
    // CONDITION: psuedoReducedTemperature < 1.2 && psuedoReducedPressure < PLIMIT && psuedoReducedPressure > 1
    else if (
      psuedoReducedTemperature < 1.2 &&
      psuedoReducedPressure < PLIMIT &&
      psuedoReducedPressure > 1
    )
      gotoSwitch = "goto40";
    // CONDITION: psuedoReducedPressure > PLIMIT && psuedoReducedTemperature > 1.2 && psuedoReducedTemperature < 1.8
    else if (
      psuedoReducedTemperature > 1.2 &&
      psuedoReducedTemperature < 1.8 &&
      psuedoReducedPressure > PLIMIT
    )
      gotoSwitch = "goto70";
    // CONDITION: psuedoReducedTemperature < 1.2 && psuedoReducedPressure > PLIMIT;
    else if (psuedoReducedTemperature < 1.2 && psuedoReducedPressure > PLIMIT)
      gotoSwitch = "goto70";

    ({
      X,
      Y,
      VISRAT_OUT: VISRAT
    } = calc_X_Y_VISRAT(params, VTEMP, VISRAT, A, B, C, IN, gotoSwitch));

    const gasViscosityCP = calcVISCO(params, VISRAT, IN);

    return {
      gasViscosityCP: Number.isFinite(gasViscosityCP) ? gasViscosityCP : 0,
      intermediates: {
        VISRAT,
        X,
        Y,
        VTEMP,
        IN,
        gotoSwitch
      }
    };
  };

// the array offsets are HIGHLY suspect even tho i have reviewed and think they are correct
const VPT = (c1: number[], iord: number, x: number, y: number) => {
  const c1_arr = c1;
  let vpt = c1_arr[0];
  for (let ii = 1; ii <= iord; ii++) {
    let nx = ii;
    const i1 = ii + 1;
    for (let ny1 = 1; ny1 <= i1; ny1++) {
      const ny = ny1 - 1;
      let xt = 1;
      if (nx !== 0) xt = x ** nx;
      let yt = 1;
      if (ny !== 0) yt = y ** ny;
      vpt += c1_arr[ii] * xt * yt;
      nx--;
    }
  }
  return vpt;
};

// FUNCTION F1(G, T)
const calcViscosityAt1ATM = (
  gasSpecificGravity: number,
  temperatureF: number
) => {
  return (
    (1.709e-5 - gasSpecificGravity * 2.062e-6) * temperatureF +
    8.188e-3 -
    Math.log(gasSpecificGravity) * 6.115e-3
  );
};
// FUNCTION FN2(D, XN2)
const calcCorrectedN2 = (D: number, XN2: number) => {
  return (0.00848 * Math.log(D) + 0.00959) * XN2;
};
// FUNCTION FC02(D, CO2)
const calcCorrectedCO2 = (D: number, CO2: number) => {
  return (0.00908 * Math.log(D) + 0.00624) * CO2;
};
// FUNCTION FH2S(D, H2S)
const calcCorrectedH2S = (D: number, H2S: number) => {
  return (0.00849 * Math.log(D) + 0.00373) * H2S;
};

// const INDEX = 0;

// the final calculation
const calcVISCO = (
  params: GasViscosityParameters,
  VISRAT: number,
  IN: number
) => {
  const {
    gasSpecificGravity,
    temperatureF,
    gasPercentages: {
      n2NitrogenPercent: XN2,
      h2SHydrogenSulfidePercent: H2S,
      cO2CarbonDioxidePercent: CO2
    }
  } = params;
  const safeGasSpecificGravity = getPositiveFiniteOrFallback(
    gasSpecificGravity,
    1
  );
  const VIS1 =
    calcViscosityAt1ATM(safeGasSpecificGravity, temperatureF) +
    (calcCorrectedN2(safeGasSpecificGravity, XN2) +
      calcCorrectedH2S(safeGasSpecificGravity, H2S) +
      calcCorrectedCO2(safeGasSpecificGravity, CO2) * IN);
  const VISCO = VIS1 * VISRAT;
  return Number.isFinite(VISCO) ? VISCO : 0;
};

// the first calculation
const calc_X_Y_VISRAT = (
  params: GasViscosityParameters,
  VTEMP: number,
  VISRAT: number,
  A: number[],
  B: number[],
  C: number[],
  IN: number,
  from: string
) => {
  const {
    psuedoCriticals: { psuedoReducedPressure, psuedoReducedTemperature }
  } = params;
  const safePsuedoReducedPressure = getPositiveFiniteOrFallback(
    psuedoReducedPressure,
    1
  );
  const safePsuedoReducedTemperature = getPositiveFiniteOrFallback(
    psuedoReducedTemperature,
    1
  );
  let X = 0;
  let Y = 0;
  let VISRAT_OUT = VISRAT;
  const VTEMP_OUT = VTEMP;

  switch (from) {
    case "goto40":
      if (IN === 0) {
        X = (Math.log(safePsuedoReducedTemperature) - 0.02118938) / 0.2218487;
        Y = (Math.log(safePsuedoReducedPressure) - 0.20412) / 1.096909;
        VISRAT_OUT = 10 ** VPT(C, 3, X, Y);
      } else {
        X = (Math.log(safePsuedoReducedTemperature) - 0.02118938) / 0.4559317;
        Y = Math.log(safePsuedoReducedPressure) / 1.301029;
        if (IN === 2) VISRAT_OUT = 10 ** VPT(B, 5, X, Y);
        else VISRAT_OUT = (VTEMP_OUT + VISRAT_OUT) / 2;
      }
      break;
    case "goto60":
      if (IN !== 1) {
        X = (Math.log(safePsuedoReducedTemperature) - 0.02118938) / 0.4559317;
        Y = (Math.log(safePsuedoReducedPressure) + 1) / 1.47712;
        VISRAT_OUT = 10 ** VPT(A, 4, X, Y);
      } else {
        X = (Math.log(safePsuedoReducedTemperature) - 0.02118938) / 0.4559317;
        Y = Math.log(safePsuedoReducedPressure) / 1.301029;
        VISRAT_OUT = (VTEMP_OUT + VISRAT_OUT) / 2;
      }
      break;
    case "goto70":
      X = (Math.log(safePsuedoReducedTemperature) - 0.02118938) / 0.2218487;
      Y = (Math.log(safePsuedoReducedPressure) - 0.20412) / 1.096909;
      VISRAT_OUT = 10 ** VPT(C, 3, X, Y);
      if (IN === 2) {
        VISRAT_OUT = (VTEMP_OUT + VISRAT_OUT) / 2;
      }
      break;
  }
  return {
    X,
    Y,
    VISRAT_OUT: Number.isFinite(VISRAT_OUT) ? VISRAT_OUT : 0,
    VTEMP_OUT
  };
};
